import { BoxGeometry, CylinderGeometry, Group, Mesh } from 'three';
import type { HouseBodyConfig, TerraceConfig } from '@/model/schema';
import type { MaterialKit } from '../materials';
import type { HingeCollector } from './buildWalls';
import type { HingeDef } from '../hinges';
import { terraceAabb } from '@/generator/extrasGenerator';
import { bakeFrameWorldUVs } from '../uv';

/**
 * A recessed upper-story CORNER terrace.
 *
 * The notch AABB is always anchored at one of the four body-plan
 * corners, so two of its four edges coincide with body facade edges
 * (the OPEN edges) and the other two are inside the body (the CLOSED
 * edges). This builder emits:
 *
 *   1. Two interior-boundary walls, one parallel to the host facade
 *      (with a door) and one parallel to the adjacent facade (solid).
 *      They meet at the notch's "inner corner" to form an L.
 *   2. A `Door_<id>` subgroup with frame + hinged leaf + knobs on the
 *      host-parallel wall. The hinge is registered so it appears in
 *      the UI panel and USDA export.
 *   3. An L-shaped railing (posts + top rail) running along BOTH open
 *      facade edges, meeting at the body corner.
 *
 * Responsibilities split:
 *   - Facade-wall skipping on the host AND adjacent sides — done by
 *     the wall builder (see `facadeSkipSpansForTerraces` in
 *     buildWalls.ts), driven by `terraceFacadeCuts` in the generator.
 *   - Slab holes on stories ABOVE the deck level — done by the main
 *     generator (`terraceSlabHolesForStory`).
 *   - Roof cutout — done by the roof builder.
 */

const WALL_THICKNESS = 0.18;
const RAILING_HEIGHT = 1.05;
const RAILING_TOP_BAR = 0.05;
const RAILING_POST_RADIUS = 0.03;
const RAILING_POST_SPACING = 0.7;

export function buildTerrace(
  body: HouseBodyConfig,
  terrace: TerraceConfig,
  storyFloorTopY: number,
  storyHeight: number,
  kit: MaterialKit,
  hinges: HingeCollector,
): Group {
  const group = new Group();
  group.name = `Terrace_L${terrace.level}_${terrace.side}`;

  const W = body.footprintWidth;
  const D = body.footprintDepth;
  const aabb = terraceAabb(body, terrace);
  const { xLo, xHi, zLo, zHi } = aabb;

  // Body-group local origin lives at body centre; body-local (0,0) is
  // the lower-left of the footprint, so bx/bz translate body-local →
  // body-group-local.
  const bx = -W / 2;
  const bz = -D / 2;
  // `storyFloorTopY` IS the slab top for upper stories. buildStory
  // centres the upper-story slab at story.elevation (slabY = 0), so
  // the slab's top face sits at `story.elevation + floorThickness/2`
  // — exactly the value passed to us.
  //
  // Surrounding exterior walls span story.elevation → elevation +
  // storyHeight (they ignore the slab). We want our terrace walls to
  // stand on the deck walking surface (slab top) and end flush with
  // those exterior walls, so the wall run is only
  // `storyHeight - floorThickness/2` tall.
  group.position.y = storyFloorTopY;

  const wallTop = storyHeight - body.floorThickness / 2;
  const wallThickness = WALL_THICKNESS;
  const halfT = wallThickness / 2;

  // Inner corner of the notch + signs pointing from the inner corner
  // INTO the notch interior. The two interior-boundary walls begin at
  // the inner corner; the two open edges face +sx / +sz from there.
  const inner = innerCorner(terrace.side, terrace.corner, aabb);
  const sx: 1 | -1 = inner.x === xLo ? 1 : -1;
  const sz: 1 | -1 = inner.z === zLo ? 1 : -1;

  const hostAlongX = terrace.side === 'front' || terrace.side === 'back';

  // Notch extent along the host facade (= `width` of the terrace).
  const hostSpan = hostAlongX ? xHi - xLo : zHi - zLo;
  // Notch extent perpendicular to host (= `depth` of the terrace).
  const adjSpan = hostAlongX ? zHi - zLo : xHi - xLo;

  const addSlab = (
    sxSize: number,
    sySize: number,
    szSize: number,
    cxLocal: number,
    cyLocal: number,
    czLocal: number,
    name: string,
  ) => {
    if (sxSize < 0.01 || sySize < 0.01 || szSize < 0.01) return;
    const geo = new BoxGeometry(sxSize, sySize, szSize);
    // Bake world-aligned UVs against the mesh's position in the
    // terrace group so the brick texture tiles continuously with
    // the host facade slabs (which share the exterior-wall material).
    bakeFrameWorldUVs(geo, cxLocal, cyLocal, czLocal);
    const mesh = new Mesh(geo, kit.exteriorWall);
    mesh.name = name;
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.position.set(cxLocal, cyLocal, czLocal);
    group.add(mesh);
  };

  // ---------- Wall along the host facade (with door) ----------
  //
  // Parameterised by u ∈ [0, hostSpan], where u=0 is at the inner
  // corner and u=hostSpan is at the body corner (where the host edge
  // meets the adjacent edge). We segment around the door.
  const doorW = terrace.doorWidth;
  const doorH = 2.05;
  const doorOff = clamp(
    terrace.doorOffsetAlongInterior,
    0.35,
    Math.max(0.35, hostSpan - doorW - 0.35),
  );

  // Emits a box along the host-parallel wall between u=[uLo, uHi] and
  // y=[yLo, yHi]. The wall sits halfT into the notch from the inner
  // edge (so its inner face is flush with the interior-boundary line).
  const emitAlongHost = (uLo: number, uHi: number, yLo: number, yHi: number, label: string) => {
    const len = Math.max(0, uHi - uLo);
    const h = Math.max(0, yHi - yLo);
    if (len < 0.01 || h < 0.01) return;
    if (hostAlongX) {
      const cx = bx + inner.x + sx * (uLo + uHi) / 2;
      const cy = (yLo + yHi) / 2;
      const cz = bz + inner.z + sz * halfT;
      addSlab(len, h, wallThickness, cx, cy, cz, `Wall_${label}`);
    } else {
      const cx = bx + inner.x + sx * halfT;
      const cy = (yLo + yHi) / 2;
      const cz = bz + inner.z + sz * (uLo + uHi) / 2;
      addSlab(wallThickness, h, len, cx, cy, cz, `Wall_${label}`);
    }
  };

  emitAlongHost(0, doorOff, 0, wallTop, 'host_leftPillar');
  emitAlongHost(doorOff, doorOff + doorW, doorH, wallTop, 'host_lintel');
  emitAlongHost(doorOff + doorW, hostSpan, 0, wallTop, 'host_rightPillar');

  // ---------- Wall along the adjacent facade (solid) ----------
  //
  // Shifted forward by wallThickness on the inner-corner end so it
  // tucks flush against the host-parallel wall without overlapping.
  // u=0 is at the inner corner, u=adjSpan at the body corner.
  const adjStart = wallThickness;
  const adjEnd = adjSpan;
  if (adjEnd - adjStart > 0.01) {
    const uLo = adjStart;
    const uHi = adjEnd;
    const len = uHi - uLo;
    const cy = wallTop / 2;
    if (hostAlongX) {
      // Wall runs along Z (the adj axis). Thickness along X.
      const cx = bx + inner.x + sx * halfT;
      const cz = bz + inner.z + sz * (uLo + uHi) / 2;
      addSlab(wallThickness, wallTop, len, cx, cy, cz, 'Wall_alongAdj');
    } else {
      const cx = bx + inner.x + sx * (uLo + uHi) / 2;
      const cz = bz + inner.z + sz * halfT;
      addSlab(len, wallTop, wallThickness, cx, cy, cz, 'Wall_alongAdj');
    }
  }

  // ---------- Door on the host-parallel wall ----------
  addTerraceDoor({
    parent: group,
    terrace,
    kit,
    hinges,
    doorOff,
    doorW,
    doorH,
    wallThickness,
    inner,
    sx,
    sz,
    hostAlongX,
    bx,
    bz,
  });

  // ---------- Railings on the two open edges ----------
  if (terrace.hasRailing) {
    addLShapedRailing(group, kit, aabb, inner, sx, sz, bx, bz);
  }

  return group;
}

/** The AABB corner farthest from the body edges — i.e. the point
 *  where the two interior-boundary walls meet. */
function innerCorner(
  side: TerraceConfig['side'],
  corner: TerraceConfig['corner'],
  a: { xLo: number; xHi: number; zLo: number; zHi: number },
): { x: number; z: number } {
  // Body corner the notch is anchored at. The inner corner of the
  // notch is the AABB corner DIAGONALLY OPPOSITE the body corner.
  switch (side) {
    case 'front':
      return corner === 'origin'
        ? { x: a.xHi, z: a.zHi }
        : { x: a.xLo, z: a.zHi };
    case 'back':
      return corner === 'origin'
        ? { x: a.xLo, z: a.zLo }
        : { x: a.xHi, z: a.zLo };
    case 'left':
      return corner === 'origin'
        ? { x: a.xHi, z: a.zLo }
        : { x: a.xHi, z: a.zHi };
    case 'right':
      return corner === 'origin'
        ? { x: a.xLo, z: a.zHi }
        : { x: a.xLo, z: a.zLo };
  }
}

interface TerraceDoorArgs {
  parent: Group;
  terrace: TerraceConfig;
  kit: MaterialKit;
  hinges: HingeCollector;
  doorOff: number;
  doorW: number;
  doorH: number;
  wallThickness: number;
  inner: { x: number; z: number };
  sx: 1 | -1;
  sz: 1 | -1;
  hostAlongX: boolean;
  bx: number;
  bz: number;
}

function addTerraceDoor(args: TerraceDoorArgs): void {
  const {
    parent,
    terrace,
    kit,
    hinges,
    doorOff,
    doorW,
    doorH,
    wallThickness,
    inner,
    sx,
    sz,
    hostAlongX,
    bx,
    bz,
  } = args;
  const frameThickness = 0.055;
  const frameDepth = 0.07;
  const halfT = wallThickness / 2;

  const doorGroup = new Group();
  doorGroup.name = `Door_terrace-L${terrace.level}-${terrace.side}-${terrace.corner}`;
  parent.add(doorGroup);

  // Door slab centre in body-group-local coordinates.
  const uCenter = doorOff + doorW / 2;
  let cxCenter: number;
  let czCenter: number;
  if (hostAlongX) {
    cxCenter = bx + inner.x + sx * uCenter;
    czCenter = bz + inner.z + sz * halfT;
  } else {
    cxCenter = bx + inner.x + sx * halfT;
    czCenter = bz + inner.z + sz * uCenter;
  }
  const cyCenter = doorH / 2;

  const frame = buildDoorFrame(
    cxCenter,
    cyCenter,
    czCenter,
    doorW,
    doorH,
    frameThickness,
    frameDepth,
    wallThickness,
    hostAlongX,
    kit,
  );
  doorGroup.add(frame);

  const innerW = doorW - 2 * frameThickness;
  const innerH = doorH - frameThickness;

  // Pivot at the low-u edge of the leaf. The leaf sits pinned to +u
  // and extends outward along (+u,+sx*hostAlongX, +sz*!hostAlongX).
  const pivotU = doorOff;
  let pivotX: number;
  let pivotZ: number;
  if (hostAlongX) {
    pivotX = bx + inner.x + sx * pivotU;
    pivotZ = czCenter;
  } else {
    pivotX = cxCenter;
    pivotZ = bz + inner.z + sz * pivotU;
  }

  // The door should swing OUT onto the terrace. The terrace interior
  // lies in +sz (for hostAlongX) or +sx (otherwise) from the host-
  // parallel wall's inner face. Since the wall is already at halfT
  // into the notch, the door leaf's resting plane is the wall's
  // midplane. Positive rotation (+Y) with a pivot on the low-u edge
  // swings the leaf toward ±u — we pick the sign that sends its free
  // edge into the terrace interior.
  const openSign = hostAlongX ? (sx * sz > 0 ? 1 : -1) : (sx * sz > 0 ? -1 : 1);

  const pivot = new Group();
  const def: HingeDef = {
    id: `terrace-door-L${terrace.level}-${terrace.side}-${terrace.corner}`,
    label: `Terrace Door · L${terrace.level} · ${terrace.side} (${terrace.corner})`,
    category: 'exterior-door',
    axis: 'Y',
    minDeg: openSign > 0 ? 0 : -100,
    maxDeg: openSign > 0 ? 100 : 0,
    openSignFromMin: openSign > 0 ? 1 : 0,
  };
  hinges.add(def);
  pivot.name = `Hinge_${def.id}`;
  pivot.userData.hinge = def;
  pivot.position.set(pivotX, 0, pivotZ);
  doorGroup.add(pivot);

  // Leaf: box whose long axis runs along the wall's u-direction.
  const leaf = new Mesh(
    new BoxGeometry(
      hostAlongX ? innerW : 0.04,
      innerH,
      hostAlongX ? 0.04 : innerW,
    ),
    kit.door,
  );
  leaf.name = 'Leaf';
  // Offset the leaf so its hinged edge sits at the pivot and its free
  // edge extends toward +u (same direction as sx for hostAlongX, sz
  // otherwise).
  if (hostAlongX) {
    leaf.position.set((sx * innerW) / 2, innerH / 2, 0);
  } else {
    leaf.position.set(0, innerH / 2, (sz * innerW) / 2);
  }
  leaf.castShadow = true;
  leaf.receiveShadow = true;
  pivot.add(leaf);

  // Knobs on both faces, near the latch (free) edge.
  const knobY = 1.05;
  const knobGeo = new CylinderGeometry(0.03, 0.03, 0.05, 12);
  for (const faceSign of [+1, -1]) {
    const knob = new Mesh(knobGeo, kit.doorKnob);
    knob.name = faceSign > 0 ? 'Knob_outside' : 'Knob_inside';
    knob.rotation.x = Math.PI / 2;
    if (hostAlongX) {
      knob.position.set(
        sx * (innerW - 0.07),
        knobY,
        faceSign * (wallThickness / 2 + 0.02),
      );
    } else {
      knob.position.set(
        faceSign * (wallThickness / 2 + 0.02),
        knobY,
        sz * (innerW - 0.07),
      );
    }
    knob.castShadow = true;
    knob.receiveShadow = true;
    pivot.add(knob);
  }
}

function buildDoorFrame(
  cxCenter: number,
  cyCenter: number,
  czCenter: number,
  doorW: number,
  doorH: number,
  thickness: number,
  depth: number,
  wallThickness: number,
  hostAlongX: boolean,
  kit: MaterialKit,
): Group {
  const g = new Group();
  g.name = 'DoorFrame';
  const halfW = doorW / 2;
  const halfH = doorH / 2;

  const makeBar = (
    sxSize: number,
    sySize: number,
    szSize: number,
    x: number,
    y: number,
    z: number,
    name: string,
  ) => {
    const m = new Mesh(new BoxGeometry(sxSize, sySize, szSize), kit.doorFrame);
    m.name = name;
    m.castShadow = true;
    m.receiveShadow = true;
    m.position.set(x, y, z);
    g.add(m);
  };

  if (hostAlongX) {
    const frameZ = Math.max(wallThickness, depth);
    makeBar(doorW, thickness, frameZ, cxCenter, cyCenter + halfH - thickness / 2, czCenter, 'Frame_top');
    makeBar(thickness, doorH - 2 * thickness, frameZ, cxCenter - halfW + thickness / 2, cyCenter, czCenter, 'Frame_left');
    makeBar(thickness, doorH - 2 * thickness, frameZ, cxCenter + halfW - thickness / 2, cyCenter, czCenter, 'Frame_right');
    makeBar(doorW, thickness, frameZ, cxCenter, cyCenter - halfH + thickness / 2, czCenter, 'Frame_bottom');
  } else {
    const frameX = Math.max(wallThickness, depth);
    makeBar(frameX, thickness, doorW, cxCenter, cyCenter + halfH - thickness / 2, czCenter, 'Frame_top');
    makeBar(frameX, doorH - 2 * thickness, thickness, cxCenter, cyCenter, czCenter - halfW + thickness / 2, 'Frame_left');
    makeBar(frameX, doorH - 2 * thickness, thickness, cxCenter, cyCenter, czCenter + halfW - thickness / 2, 'Frame_right');
    makeBar(frameX, thickness, doorW, cxCenter, cyCenter - halfH + thickness / 2, czCenter, 'Frame_bottom');
  }
  return g;
}

/** Two railing runs on the two open facade edges of the notch, meeting
 *  at the body corner. Each run is inset by a few cm from the exact
 *  facade plane to avoid z-fighting with the facade geometry above/
 *  below the terrace. */
function addLShapedRailing(
  parent: Group,
  kit: MaterialKit,
  aabb: { xLo: number; xHi: number; zLo: number; zHi: number },
  inner: { x: number; z: number },
  sx: 1 | -1,
  sz: 1 | -1,
  bx: number,
  bz: number,
): void {
  const g = new Group();
  g.name = 'TerraceRailing';
  parent.add(g);

  const INSET = 0.06;

  // Host-open edge runs along the HOST axis at the OPEN adj-side of
  // the AABB. For sx=+1 the open adj-x edge is at x=xHi; the edge's x
  // coordinate is inner.x + sx * adjSpan. Similarly for sz.
  // We bake the edges out explicitly: endpoints of the two runs share
  // the body corner.
  const adjSpanX = aabb.xHi - aabb.xLo;
  const adjSpanZ = aabb.zHi - aabb.zLo;

  // The body corner (where both open edges meet).
  const cornerX = inner.x + sx * adjSpanX;
  const cornerZ = inner.z + sz * adjSpanZ;

  // Host-parallel run: along whichever axis the host facade runs.
  // It sits at the INNER.z (or inner.x) plus adjSpan from the inner
  // corner to the body corner.
  const runs: Array<{
    alongX: boolean;
    x0: number; z0: number;
    x1: number; z1: number;
  }> = [];

  // Run 1: open host edge — from (inner.x, cornerZ) to (cornerX, cornerZ)
  // (for hostAlongX) OR (cornerX, inner.z) to (cornerX, cornerZ) (else).
  // Regardless of host orientation we can express BOTH runs uniformly
  // as: one edge from inner corner along +sx at z=cornerZ, and one
  // edge along +sz at x=cornerX. That's exactly the two L-arms.
  //
  // Inset: push each run toward the notch interior by INSET on the
  // perpendicular axis.
  // Run along X at z=cornerZ (-sz inset):
  runs.push({
    alongX: true,
    x0: bx + inner.x,
    x1: bx + cornerX,
    z0: bz + cornerZ - sz * INSET,
    z1: bz + cornerZ - sz * INSET,
  });
  // Run along Z at x=cornerX (-sx inset):
  runs.push({
    alongX: false,
    x0: bx + cornerX - sx * INSET,
    x1: bx + cornerX - sx * INSET,
    z0: bz + inner.z,
    z1: bz + cornerZ,
  });

  for (const run of runs) {
    const length = run.alongX
      ? Math.abs(run.x1 - run.x0)
      : Math.abs(run.z1 - run.z0);
    if (length <= 0.1) continue;

    // Top rail.
    const topY = RAILING_HEIGHT;
    const rail = new Mesh(
      new BoxGeometry(
        run.alongX ? length : RAILING_TOP_BAR,
        RAILING_TOP_BAR,
        run.alongX ? RAILING_TOP_BAR : length,
      ),
      kit.fence,
    );
    rail.name = run.alongX ? 'TopRail_host' : 'TopRail_adj';
    rail.castShadow = true;
    rail.receiveShadow = true;
    rail.position.set((run.x0 + run.x1) / 2, topY, (run.z0 + run.z1) / 2);
    g.add(rail);

    // Posts.
    const postCount = Math.max(2, Math.ceil(length / RAILING_POST_SPACING) + 1);
    for (let i = 0; i < postCount; i++) {
      const t = i / (postCount - 1);
      const px = run.alongX ? run.x0 + (run.x1 - run.x0) * t : run.x0;
      const pz = run.alongX ? run.z0 : run.z0 + (run.z1 - run.z0) * t;
      const post = new Mesh(
        new CylinderGeometry(RAILING_POST_RADIUS, RAILING_POST_RADIUS, RAILING_HEIGHT, 8),
        kit.fence,
      );
      post.name = `Post_${run.alongX ? 'host' : 'adj'}_${i.toString().padStart(2, '0')}`;
      post.castShadow = true;
      post.receiveShadow = true;
      post.position.set(px, RAILING_HEIGHT / 2, pz);
      g.add(post);
    }
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}
